import isFunction from 'lodash/isFunction';
import UIkit from 'uikit';
import Authentication from '@/mixins/Authentication';
import DataGrid from '@/components/DataGrid';
import DataGridActions from '@/components/DataGridActions';
import FilterableView from '@/mixins/FilterableView';
import Loader from '@/components/Loader';
import PaginatedView from '@/mixins/PaginatedView';
import Paginator from '@/components/Paginator';
import Progress from '@/mixins/Progress';
import SearchableView from '@/mixins/SearchableView';
import SortedView from '@/mixins/SortedView';
import Toast from '@/helpers/Toast';
import View from '@/mixins/View';

export default (name, mixinOptions = {}) => {
  const gridFields = mixinOptions.gridFields || [];
  const useInternalFilter = mixinOptions.useInternalFilter || false;
  const useInternalSearch = mixinOptions.useInternalSearch || useInternalFilter;
  const useInternalSort = mixinOptions.useInternalSort || useInternalFilter;
  const useInternalPagination =
    mixinOptions.useInternalPagination || useInternalFilter;

  const defaultFilter = mixinOptions.defaultFilter || {};

  const filterableViewOptions = {
    definitions: mixinOptions.filterParams || {},
    useInternalFilter,
  };

  const sortableViewOptions = {
    defaultSort: mixinOptions.defaultSort,
    useInternalSort,
  };

  return {
    mixins: [
      Authentication,
      FilterableView(filterableViewOptions),
      PaginatedView({
        useInternalPagination,
      }),
      Progress,
      SearchableView({
        useInternalSearch,
      }),
      SortedView(sortableViewOptions),
      View,
    ],
    components: {
      DataGrid,
      DataGridActions,
      Loader,
      Paginator,
    },
    data() {
      return {
        gridLoaded: false,
      };
    },
    mounted() {
      const searchInput = this.$refs.search;
      if (searchInput && isFunction(searchInput.focus)) {
        searchInput.focus();
      }
    },
    computed: {
      gridColumns() {
        const columns = {};
        gridFields.forEach((fieldName) => {
          columns[fieldName] = this.$t(`fields.${fieldName}`);
        });
        return columns;
      },
      additionalQueryVariables() {
        return {};
      },
      queryVariables() {
        return Object.assign({
          filter: this.filterInfoWithDefault,
          pagination: this.pageInfo,
          sort: this.sortInfo,
          search: this.searchQuery,
        }, this.additionalQueryVariables);
      },
      filterInfoWithDefault() {
        let filter = defaultFilter;
        if (isFunction(defaultFilter)) {
          filter = defaultFilter.call(this);
        }
        return {
          ...filter,
          ...this.filterInfo,
        };
      },
      skipQuery() {
        return false;
      },
    },
    apollo: {
      [name]: {
        query: mixinOptions.listQuery,
        variables() {
          return this.queryVariables;
        },
        loadingKey: 'loading',
        fetchPolicy: 'network-only',
        result({
          data,
        }) {
          this.gridLoaded = true; // indicates only if the grid has loaded once
          const pagination = data[name].pagination;
          if (pagination) {
            this.redirectFromInvalidPage(pagination);
          }
          if (mixinOptions.pollInterval) {
            this.$apollo.queries[name].startPolling(mixinOptions.pollInterval);
          }
        },
        skip() {
          return this.skipQuery || !this.isAuthenticated;
        },
      },
    },
    methods: {
      refetchList() {
        this.$apollo.queries[name].refetch();
      },
      resetInternals() {
        this.resetInternalFilter();
        this.resetInternalPagination();
        this.resetInternalSearch();
        this.resetInternalSort();
      },
      mutationVariables(record) {
        return {
          id: record.id,
        };
      },
      getFilter() {
        const filter = mixinOptions.filter;
        if (isFunction(filter)) return filter.call(this);
        return filter;
      },
      getRecordName(record) {
        const field = mixinOptions.nameField || mixinOptions.gridFields[0];
        if (isFunction(field)) {
          return field(record);
        }
        return record[field];
      },
      async deleteRecordWhenConfirmed(record) {
        const recordName = this.getRecordName(record);
        const message = this.$t('confirmations.softDelete', {
          name: recordName,
        });
        try {
          await UIkit.modal.confirm(message);
          this.deleteRecord(record);
        } catch (_) {
          // Do nothing
        }
      },
      async undeleteRecord(record, refetch = true) {
        const options = {
          mutation: mixinOptions.undeleteMutation,
          variables: this.mutationVariables(record),
        };

        if (refetch) {
          options.refetchQueries = [{
            query: mixinOptions.listQuery,
            variables: this.queryVariables,
          }];
        }

        await this.$apollo.mutate(options);

        const message = this.$t('toasts.undeleted', {
          name: record.name,
        });
        Toast.success(message);
      },
      async deleteRecord(record, refetch = true) {
        const options = {
          mutation: mixinOptions.deleteMutation,
          variables: this.mutationVariables(record),
        };
        if (refetch) {
          options.refetchQueries = [{
            query: mixinOptions.listQuery,
            variables: this.queryVariables,
          }];
        }
        await this.$apollo.mutate(options);
        const recordName = this.getRecordName(record);
        const message = this.$t('toasts.deleted', {
          name: recordName,
        });
        Toast.success(message);
      },
    },
  };
};



// WEBPACK FOOTER //
// ./mixins/IndexView.js